import { ApiError, SuccessOrError } from '@upright/common/types-api';
import { buildRequestAndFetch } from '../fetcher-core';
import { getJsonOrUndefined } from '../response';
import { ApiResponse, getErrorMessage } from '../types';
import { maybeHandleCustomError } from '../util';

/**
 * With Tanstack Query, use [fetchOrThrow]({@link ./fetch-or-throw.ts}) instead.
 */
export async function fetchApiWithError<T>(path: string, method = 'GET', bodyContent?: any): Promise<ApiResponse<T>> {
  let response: Response;
  try {
    response = await buildRequestAndFetch(path, method, bodyContent);
  } catch (error) {
    const message = getErrorMessage(error);
    return {
      data: undefined,
      status: 0,
      statusText: message,
    };
  }
  const data = await getJsonOrUndefined<T>(response);
  if (!response.ok) await maybeHandleCustomError(data);
  return {
    data,
    status: response.status,
    statusText: response.statusText,
  };
}

// Alternative version, used in some endpoints. Actually tries to dig message from response json
// (such as BoomError)
export async function requestApiWithError<T>(
  path: string,
  method = 'GET',
  bodyContent?: any,
): Promise<{ data: T | undefined; apiError: ApiError | undefined }> {
  let response: Response;
  try {
    response = await buildRequestAndFetch(path, method, bodyContent);
    if (response.ok) {
      const data = (await response.json()) as T;
      return {
        data,
        apiError: undefined,
      };
    } else {
      const responseJson = await getJsonOrUndefined<unknown>(response);
      await maybeHandleCustomError(responseJson);
      const message = getErrorMessage(responseJson);
      return {
        data: undefined,
        apiError: { message, status: response.status, statusText: response.statusText },
      };
    }
  } catch (error) {
    return {
      data: undefined,
      apiError: { status: 0, statusText: 'Unable to connect to server' },
    };
  }
}

/**
 * Smartly wrap an ApiResponse<SuccessOrError> promise
 *
 * Many legacy components use a SuccessOrError object
 * This can be used to wrap a promise for an server endpoint
 * that returns SuccessOrError objects to one which also
 * properly shows error indication for problems communicating
 * with the server
 */
export async function toSmartSuccessOrError(p: Promise<ApiResponse<SuccessOrError>>): Promise<SuccessOrError> {
  const res = await p;
  if (!res.status) {
    return {
      success: false,
      error: 'Could not reach server',
    };
  }
  if ((res.status !== 200 && res.status !== 201) || !res.data) {
    return {
      success: false,
      error: `Unexpected error`,
    };
  }
  return res.data;
}
